import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Search, ArrowLeft, Package, Upload, Store, ChevronLeft, ChevronRight, RotateCcw, Coins, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import AdminOrderRow from '@/components/admin/AdminOrderRow';
import LegacyOrderImportModal from '@/components/admin/LegacyOrderImportModal';
import ImportPosSalesModal from '@/components/admin/ImportPosSalesModal';
import PaymentReconciliationModal from '@/components/admin/PaymentReconciliationModal';
import OrdersKpiStrip from '@/components/admin/OrdersKpiStrip';
import AdminOrdersBulkBar from '@/components/admin/AdminOrdersBulkBar';
import { downloadPickingList } from '@/lib/pickingSlips';
import { exportOrderMarginsCsv } from '@/utils/exportOrderMarginsCsv';
import BulkAssignAccountModal from '@/components/admin/BulkAssignAccountModal';
import GiftCardReceiptsTab from '@/components/admin/GiftCardReceiptsTab';
import DepositsTab from '@/components/admin/DepositsTab';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/lib/AuthContext';

// The list opens on the paid grouping so unpaid and abandoned checkouts no
// longer sit alongside real takings. Every individual status stays selectable.
const PAID_STATUSES = [
  'paid', 'in_production', 'packed', 'dispatched', 'delivered',
  'completed', 'refunded', 'partially_refunded',
];

const STATUS_FILTERS = [
  'paid_sales', 'all', 'pending', 'paid', 'failed', 'in_production', 'packed',
  'dispatched', 'delivered', 'completed', 'refunded', 'partially_refunded',
];

const PAGE_SIZE = 25;

function startOfDay(d) { const t = new Date(d); t.setHours(0,0,0,0); return t.getTime(); }
function endOfDay(d) { const t = new Date(d); t.setHours(23,59,59,999); return t.getTime(); }

export default function AdminOrders() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('paid_sales');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPosImport, setShowPosImport] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [tab, setTab] = useState('sales');
  const [backfilling, setBackfilling] = useState(false);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['admin-orders', 'all'],
    // Records folded into a master order by the merge tool are retained for
    // audit but hidden here so the sale is only ever counted once.
    queryFn: async () => (await base44.entities.Order.list('-created_date', 500))
      .filter((o) => !o.merged_into_order_id),
    enabled: currentUser?.role === 'admin',
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const q = search.toLowerCase();
  const fromTs = dateFrom ? startOfDay(dateFrom) : null;
  const toTs = dateTo ? endOfDay(dateTo) : null;

  const filtered = orders.filter((o) => {
    if (statusFilter === 'paid_sales') {
      if (!PAID_STATUSES.includes(o.status)) return false;
    } else if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (fromTs || toTs) {
      const t = o.created_date ? new Date(o.created_date).getTime() : null;
      if (t == null) return false;
      if (fromTs && t < fromTs) return false;
      if (toTs && t > toTs) return false;
    }
    if (!q) return true;
    return [
      o.customer_name, o.customer_email, o.product_name,
      o.invoice_number, o.id, o.tracking_number,
      o.consumer_account_id, o.legacy_order_id,
    ].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageIds = pageItems.map((o) => o.id);
  const allSelectedOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    queryClient.invalidateQueries({ queryKey: ['admin-orders-for-stats'] });
  };

  const onBulkStatus = async (newStatus) => {
    setBusy(true);
    const ids = [...selected];
    try {
      for (const id of ids) {
        await base44.functions.invoke('adminUpdateOrder', {
          orderId: id,
          patch: { status: newStatus },
          auditAction: 'bulk_status_change',
          auditDescription: `Bulk status changed to ${newStatus}`,
        });
      }
      refresh();
      clearSelection();
      toast({ title: `${ids.length} order${ids.length !== 1 ? 's' : ''} marked ${newStatus.replace(/_/g, ' ')}` });
    } catch (e) {
      toast({ title: 'Bulk update failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onBulkSageExport = async () => {
    setBusy(true);
    try {
      const res = await base44.functions.invoke('exportSalesToSage', { order_ids: [...selected] });
      const csv = res.data?.csv || '';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sage-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      refresh();
      clearSelection();
    } catch (e) {
      toast({ title: 'Sage export failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const resetFilters = () => {
    setSearch(''); setStatusFilter('paid_sales'); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const onBulkSendStatusEmail = async () => {
    setBusy(true);
    let sent = 0;
    let failed = 0;
    for (const id of [...selected]) {
      try {
        await base44.functions.invoke('orderStatusEmail', { manual: true, orderId: id });
        sent++;
      } catch (e) { failed++; }
    }
    setBusy(false);
    toast({ title: `${sent} status email${sent !== 1 ? 's' : ''} sent`, description: failed ? `${failed} failed` : undefined, variant: failed ? 'destructive' : 'default' });
  };

  const onBulkPickingList = async () => {
    setBusy(true);
    try {
      const data = await downloadPickingList([...selected]);
      toast({
        title: 'Picking list generated',
        description: `${data.count} order${data.count !== 1 ? 's' : ''}${data.truncated ? ' (capped at 100)' : ''}`,
      });
    } catch (e) {
      toast({ title: 'Picking list failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onBulkMarginExport = () => {
    const pool = orders.filter((o) => selected.has(o.id));
    const rows = exportOrderMarginsCsv(pool);
    toast({
      title: 'Margin CSV exported',
      description: `${rows} line${rows !== 1 ? 's' : ''} across ${pool.length} sale${pool.length !== 1 ? 's' : ''}.`,
    });
  };

  // Fills cost onto historic sales from each product's current cost price. The
  // affected sales are marked as a backfilled estimate so they are never confused
  // with the cost snapshotted at the time of sale.
  const onBackfillCosts = async () => {
    setBackfilling(true);
    try {
      const res = await base44.functions.invoke('backfillOrderCosts', {});
      const d = res.data || {};
      if (d.success) {
        toast({
          title: `${d.orders_updated} sale${d.orders_updated !== 1 ? 's' : ''} costed`,
          description: `${d.lines_filled} line${d.lines_filled !== 1 ? 's' : ''} filled from current cost prices · ${d.skipped} left as they were.`,
        });
        refresh();
      } else {
        toast({ title: 'Backfill failed', description: d.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Backfill failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setBackfilling(false);
    }
  };

  const onBulkMerge = async () => {
    setBusy(true);
    try {
      const res = await base44.functions.invoke('adminMergeOrders', { order_ids: [...selected] });
      const d = res.data || {};
      if (d.success) {
        toast({
          title: `Merged ${d.mergedCount + 1} records into one order`,
          description: `Total £${Number(d.amount).toFixed(2)} · invoice ${d.masterInvoiceNumber || '—'}`
            + (d.voidedInvoiceNumbers?.length ? ` · voided ${d.voidedInvoiceNumbers.join(', ')}` : ''),
        });
        refresh();
        clearSelection();
      } else {
        toast({ title: 'Merge failed', description: d.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Merge failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onBulkAssignAccount = async (accountId, accountName) => {
    setBusy(true);
    try {
      await base44.entities.Order.bulkUpdate([...selected].map((id) => ({ id, consumer_account_id: accountId })));
      toast({ title: `Assigned ${selected.size} order${selected.size !== 1 ? 's' : ''} to ${accountName}` });
      refresh();
      clearSelection();
      setShowAssign(false);
    } catch (e) {
      toast({ title: 'Assign failed', description: e.message, variant: 'destructive' });
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16 pb-24">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">REVENUE</p>
            <h1 className="text-white font-black text-3xl md:text-4xl uppercase">SALES</h1>
            <p className="text-white/30 text-sm mt-1">{orders.length} sale{orders.length !== 1 ? 's' : ''} total · {filtered.length} shown</p>
            <p className="text-white/30 text-xs mt-2">
              Logged in as: <span className="text-white font-medium">{currentUser.first_name || currentUser.full_name?.split(' ')[0]} ({currentUser.email})</span>
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-10 max-w-6xl mx-auto">
        {/* Tab toggle: Sales (taxable orders) vs Gift Card Receipts (non-taxable) */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/5">
          <button
            onClick={() => setTab('sales')}
            className={`px-4 py-2.5 text-[11px] font-bold tracking-wider transition-colors ${tab === 'sales' ? 'text-primary border-b-2 border-primary' : 'text-white/30 hover:text-white/60'}`}
          >
            SALES
          </button>
          <button
            onClick={() => setTab('receipts')}
            className={`px-4 py-2.5 text-[11px] font-bold tracking-wider transition-colors ${tab === 'receipts' ? 'text-primary border-b-2 border-primary' : 'text-white/30 hover:text-white/60'}`}
          >
            GIFT CARD RECEIPTS
          </button>
          <button
            onClick={() => setTab('deposits')}
            className={`px-4 py-2.5 text-[11px] font-bold tracking-wider transition-colors ${tab === 'deposits' ? 'text-primary border-b-2 border-primary' : 'text-white/30 hover:text-white/60'}`}
          >
            DEPOSITS
          </button>
        </div>

        {tab === 'receipts' ? (
          <GiftCardReceiptsTab />
        ) : tab === 'deposits' ? (
          <DepositsTab />
        ) : (
        <>
        {/* KPI strip */}
        <OrdersKpiStrip orders={orders} marginOrders={filtered} />

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search customer, email, product, invoice, order no, account ref..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-[#111115] border border-white/10 text-white text-xs font-bold h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s} className="bg-[#111115]">
                {s === 'all' ? 'ALL STATUSES' : s.replace(/_/g, ' ').toUpperCase()}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-xs tracking-wider h-11 px-4 transition-colors shrink-0"
          >
            <Upload className="w-3.5 h-3.5" /> IMPORT LEGACY
          </button>
          <button
            onClick={() => setShowPosImport(true)}
            title="Fetch recent Square POS sales and import them as orders"
            className="flex items-center gap-1.5 bg-orange-400/10 hover:bg-orange-400/20 border border-orange-400/30 text-orange-400 font-bold text-xs tracking-wider h-11 px-4 transition-colors shrink-0"
          >
            <Store className="w-3.5 h-3.5" /> IMPORT POS
          </button>
          <button
            onClick={() => setShowReconcile(true)}
            title="Check the payments taken against the sales recorded here, and clear any duplicates"
            className="flex items-center gap-1.5 bg-red-400/10 hover:bg-red-400/20 border border-red-400/30 text-red-400 font-bold text-xs tracking-wider h-11 px-4 transition-colors shrink-0"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> RECONCILE PAYMENTS
          </button>
          <button
            onClick={onBackfillCosts}
            disabled={backfilling}
            title="Fill cost prices onto historic sales from each product's current cost, so their COGS and margin can be reported"
            className="flex items-center gap-1.5 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 text-cyan-400 font-bold text-xs tracking-wider h-11 px-4 transition-colors shrink-0 disabled:opacity-50"
          >
            {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />} BACKFILL COSTS
          </button>
        </div>

        {/* Date range */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-white/30 text-[10px] font-bold tracking-widest">FROM</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="bg-[#111115] border border-white/10 text-white text-xs h-9 px-2 focus:border-primary/60 outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/30 text-[10px] font-bold tracking-widest">TO</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="bg-[#111115] border border-white/10 text-white text-xs h-9 px-2 focus:border-primary/60 outline-none"
            />
          </div>
          {(dateFrom || dateTo || search || statusFilter !== 'paid_sales') && (
            <button onClick={resetFilters} className="flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-wider">
              <RotateCcw className="w-3 h-3" /> RESET FILTERS
            </button>
          )}
        </div>

        {showImport && <LegacyOrderImportModal onClose={() => setShowImport(false)} />}
        {showPosImport && <ImportPosSalesModal onClose={() => setShowPosImport(false)} />}
        {showReconcile && <PaymentReconciliationModal onClose={() => setShowReconcile(false)} />}

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Package className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No orders found</p>
          </div>
        ) : (
          <>
            <div className="border border-white/5 overflow-hidden">
              <div className="hidden sm:flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 bg-[#111115] border-b border-white/5">
                <p className="w-8 shrink-0" />
                <p className="w-6 shrink-0" />
                <p className="text-white/30 text-[10px] font-bold tracking-widest w-16 sm:w-20 shrink-0">ORDER</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 shrink-0 hidden md:block">DATE</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1">CUSTOMER</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1 hidden sm:block">PRODUCT</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest w-16 sm:w-20 text-right shrink-0">TOTAL</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 text-right shrink-0 hidden md:block">MARGIN</p>
                <p className="text-white/30 text-[10px] font-bold tracking-widest w-8 sm:w-32 text-center shrink-0">STATUS</p>
                <p className="w-6 shrink-0" />
              </div>
              {pageItems.map((order) => (
                <AdminOrderRow
                  key={order.id}
                  order={order}
                  userEmail="all"
                  selected={selected.has(order.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-white/30 text-xs">
                Page {safePage} of {totalPages} · {filtered.length} orders
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="flex items-center gap-1 border border-white/10 hover:border-primary/40 text-white/60 hover:text-white text-xs font-bold tracking-wider px-3 h-9 disabled:opacity-30"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> PREV
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="flex items-center gap-1 border border-white/10 hover:border-primary/40 text-white/60 hover:text-white text-xs font-bold tracking-wider px-3 h-9 disabled:opacity-30"
                >
                  NEXT <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        )}
        </>
        )}
      </section>

      <AdminOrdersBulkBar
        selectedCount={selected.size}
        allSelectedOnPage={allSelectedOnPage}
        onSelectAllOnPage={toggleSelectPage}
        onClear={clearSelection}
        onBulkStatus={onBulkStatus}
        onBulkSageExport={onBulkSageExport}
        onBulkSendStatusEmail={onBulkSendStatusEmail}
        onBulkPickingList={onBulkPickingList}
        onBulkMarginExport={onBulkMarginExport}
        onAssignAccount={() => setShowAssign(true)}
        onBulkMerge={onBulkMerge}
        busy={busy}
      />
      {showAssign && (
        <BulkAssignAccountModal onClose={() => setShowAssign(false)} onPick={onBulkAssignAccount} />
      )}
    </div>
  );
}